import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { 
  ArrowLeft, 
  Shield, 
  Plus, 
  Trash2, 
  GripVertical,
  Eye,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPDPASettings({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  const [settings, setSettings] = useState({
    version: 0,
    main_header: "By using this portal, you acknowledge and agree to the following:",
    clauses: [],
    active: true
  });
  
  const [acknowledgments, setAcknowledgments] = useState([]);
  const [showAuditLog, setShowAuditLog] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchAcknowledgments();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/pdpa/settings`, { withCredentials: true });
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching PDPA settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAcknowledgments = async () => {
    try {
      const response = await axios.get(`${API}/admin/pdpa/acknowledgments`, { withCredentials: true });
      setAcknowledgments(response.data);
    } catch (error) {
      console.error("Error fetching acknowledgments:", error);
    }
  };

  const handleSave = async () => {
    if (!settings.main_header.trim()) {
      toast.error("Main header is required");
      return;
    }
    
    if (settings.clauses.length === 0) {
      toast.error("At least one clause is required");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(`${API}/admin/pdpa/settings`, settings, { withCredentials: true });
      toast.success(response.data.message);
      if (response.data.clauses_changed) {
        toast.info(`Version updated to ${response.data.version}. All users will need to re-acknowledge.`);
      }
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const addClause = () => {
    const newClause = {
      id: `clause_${Date.now()}`,
      text: "",
      required: true,
      order: settings.clauses.length
    };
    setSettings({
      ...settings,
      clauses: [...settings.clauses, newClause]
    });
  };

  const updateClause = (index, field, value) => {
    const updated = [...settings.clauses];
    updated[index] = { ...updated[index], [field]: value };
    setSettings({ ...settings, clauses: updated });
  };

  const removeClause = (index) => {
    if (!window.confirm("Remove this clause?")) return;
    const updated = settings.clauses.filter((_, i) => i !== index);
    setSettings({ ...settings, clauses: updated });
  };

  const moveClause = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= settings.clauses.length) return;
    
    const updated = [...settings.clauses];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    // Update order
    updated.forEach((c, i) => c.order = i);
    setSettings({ ...settings, clauses: updated });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-yellow-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin")}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-400" />
              PDPA / Consent Management
            </h1>
            <p className="text-slate-400 text-sm">Configure consent clauses that members must agree to</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPreview(!showPreview)}
              className="border-slate-600 text-slate-300"
            >
              <Eye className="w-4 h-4 mr-2" />
              {showPreview ? "Hide Preview" : "Preview"}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Status Card */}
        <Card className="p-4 bg-slate-800/50 border-slate-700 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-3 h-3 rounded-full ${settings.active ? 'bg-green-400' : 'bg-red-400'}`}></div>
              <div>
                <p className="text-white font-medium">
                  Status: {settings.active ? 'Active' : 'Inactive'}
                </p>
                <p className="text-slate-400 text-sm">Version {settings.version}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-slate-400 text-sm">Enable PDPA</span>
              <Switch
                checked={settings.active}
                onCheckedChange={(checked) => setSettings({ ...settings, active: checked })}
              />
            </div>
          </div>
          {acknowledgments.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-700">
              <button
                onClick={() => setShowAuditLog(!showAuditLog)}
                className="text-blue-400 text-sm hover:underline"
              >
                {acknowledgments.length} members have acknowledged • {showAuditLog ? 'Hide' : 'View'} audit log
              </button>
            </div>
          )}
        </Card>

        {/* Audit Log */}
        {showAuditLog && (
          <Card className="p-4 bg-slate-800/50 border-slate-700 mb-6 max-h-64 overflow-y-auto">
            <h3 className="font-semibold text-white mb-3">Acknowledgment Audit Log</h3>
            <div className="space-y-2">
              {acknowledgments.map((ack, i) => (
                <div key={i} className="flex justify-between text-sm border-b border-slate-700 pb-2">
                  <div>
                    <span className="text-white">{ack.user_name || ack.user_email}</span>
                    <span className="text-slate-500 ml-2">v{ack.pdpa_version}</span>
                  </div>
                  <div className="text-slate-400">
                    {new Date(ack.agreed_at).toLocaleString()}
                    {ack.dont_show_again && <span className="ml-2 text-yellow-400">(opted out)</span>}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Main Header */}
        <Card className="p-6 bg-slate-800/50 border-slate-700 mb-6">
          <label className="text-sm font-medium text-slate-300 mb-2 block">
            Main Header / Introduction
          </label>
          <Textarea
            value={settings.main_header}
            onChange={(e) => setSettings({ ...settings, main_header: e.target.value })}
            placeholder="Enter the main message that appears before the checkboxes..."
            className="bg-slate-900 border-slate-600 text-white min-h-[100px]"
          />
          <p className="text-xs text-slate-500 mt-2">
            This text appears at the top of the consent form before the checkboxes.
          </p>
        </Card>

        {/* Clauses */}
        <Card className="p-6 bg-slate-800/50 border-slate-700 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-white">Consent Clauses</h3>
            <Button
              onClick={addClause}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Clause
            </Button>
          </div>
          
          {settings.clauses.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
              <p>No clauses added yet. Add at least one clause.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {settings.clauses.map((clause, index) => (
                <div 
                  key={clause.id} 
                  className="flex gap-3 p-4 bg-slate-900/50 rounded-lg border border-slate-700"
                >
                  {/* Drag Handle & Order */}
                  <div className="flex flex-col items-center gap-1">
                    <button
                      onClick={() => moveClause(index, -1)}
                      disabled={index === 0}
                      className="text-slate-500 hover:text-white disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <GripVertical className="w-4 h-4 text-slate-600" />
                    <button
                      onClick={() => moveClause(index, 1)}
                      disabled={index === settings.clauses.length - 1}
                      className="text-slate-500 hover:text-white disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>
                  
                  {/* Clause Content */}
                  <div className="flex-1 space-y-3">
                    <Textarea
                      value={clause.text}
                      onChange={(e) => updateClause(index, 'text', e.target.value)}
                      placeholder="Enter clause text..."
                      className="bg-slate-800 border-slate-600 text-white min-h-[80px]"
                    />
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={clause.required}
                          onCheckedChange={(checked) => updateClause(index, 'required', checked)}
                        />
                        <span className="text-sm text-slate-300">Required</span>
                      </label>
                      {clause.required && (
                        <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">
                          Must be checked to proceed
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Delete */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeClause(index)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Preview */}
        {showPreview && (
          <Card className="p-6 bg-white border-2 border-blue-500">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              Preview: How Members Will See This
            </h3>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-800 mb-6 whitespace-pre-wrap">{settings.main_header}</p>
              
              <div className="space-y-4">
                {settings.clauses.map((clause, index) => (
                  <label key={index} className="flex items-start gap-3 cursor-pointer">
                    <Checkbox disabled className="mt-1" />
                    <div>
                      <span className="text-gray-700">{clause.text || '(empty clause)'}</span>
                      {clause.required && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-200">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
                  <Checkbox disabled />
                  <span>Don't show this again (until terms change)</span>
                </label>
              </div>
              
              <div className="mt-6 flex gap-3">
                <Button disabled className="flex-1 bg-gray-300 text-gray-500">
                  I Agree & Continue
                </Button>
              </div>
              
              <p className="text-xs text-gray-500 mt-4 text-center">
                * Required fields must be checked to proceed
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
